import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Store, Users, User, UserCog, Edit, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import RolesAndPermissionsView from './RolesAndPermissionsView';
import UsersTab from './UsersTab';
import InformationsTab from './InformationsTab';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const TABS = [
  { key: 'utilisateurs', label: 'Utilisateurs' },
  { key: 'roles', label: 'Rôles & Permissions' },
  { key: 'informations', label: 'Informations' },
];

function UsersManagement({ currentUser }) {
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [rolesSummary, setRolesSummary] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('utilisateurs');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    fetchPharmacies();
  }, [currentUser]);

  useEffect(() => {
    if (selectedPharmacy) {
      fetchUsers();
    }
  }, [selectedPharmacy]);

  const getHeaders = () => ({
    'X-User-Id': currentUser?.id || '',
    'X-Pharmacy-Id': selectedPharmacy?.id || currentUser?.pharmacies?.[0]?.pharmacy_id || '',
    'X-Session-Token': currentUser?.session_token || ''
  });

  const fetchPharmacies = async () => {
    try {
      if (currentUser?.role !== 'super_admin') {
        const userPharms = currentUser?.pharmacies?.map(p => ({ id: p.pharmacy_id, name: p.pharmacy_name })) || [];
        setPharmacies(userPharms);
        if (userPharms.length > 0) setSelectedPharmacy(userPharms[0]);
        return;
      }
      const headers = { 'X-User-Id': currentUser?.id || '' };
      const response = await axios.get(`${API}/pharmacies`, { headers });
      const pharms = response.data;
      setPharmacies(pharms);
      if (pharms.length > 0) {
        setSelectedPharmacy(pharms[0]);
      } else if (currentUser?.pharmacies?.length > 0) {
        const userPharms = currentUser.pharmacies.map(p => ({ id: p.pharmacy_id, name: p.pharmacy_name }));
        setPharmacies(userPharms);
        setSelectedPharmacy(userPharms[0]);
      }
    } catch (error) {
      console.error('Error fetching pharmacies:', error);
      if (currentUser?.pharmacies?.length > 0) {
        const userPharms = currentUser.pharmacies.map(p => ({ id: p.pharmacy_id, name: p.pharmacy_name }));
        setPharmacies(userPharms);
        setSelectedPharmacy(userPharms[0]);
      }
    }
  };

  const fetchUsers = async () => {
    if (!selectedPharmacy) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users?t=${Date.now()}`, { headers: getHeaders() });
      const userPharmaciesRes = await axios.get(`${API}/user-pharmacies-for-pharmacy/${selectedPharmacy.id}?t=${Date.now()}`, { headers: getHeaders() }).catch(() => ({ data: [] }));
      const userPharmaciesData = userPharmaciesRes.data;
      const allUsers = response.data;
      const usersWithRoles = allUsers.map(user => {
        const userPharmacy = userPharmaciesData.find(up => up.user_id === user.id);
        const userPharm = user.pharmacies?.find(p => p.pharmacy_id === selectedPharmacy.id);
        let role = userPharmacy?.role || userPharm?.role || user.role || 'caissier';
        if (role === 'user' || !['super_admin', 'admin', 'owner', 'gerant', 'comptable', 'caissier', 'assistant'].includes(role.toLowerCase().trim())) {
          role = 'caissier';
        }
        return {
          ...user,
          role,
          permissions: userPharmacy?.permissions || user.granular_permissions || {}
        };
      }).filter(u =>
        userPharmaciesData.some(up => up.user_id === u.id) ||
        u.pharmacies?.some(p => p.pharmacy_id === selectedPharmacy.id) ||
        u.role === 'super_admin'
      );
      setUsers(usersWithRoles);
      const summary = {};
      usersWithRoles.forEach(u => {
        const r = u.role;
        summary[r] = (summary[r] || 0) + 1;
      });
      setRolesSummary(Object.keys(summary).map(key => ({ role: key, count: summary[key] })));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => fetchUsers();

  const getRoleCount = (roleId) => {
    const summary = rolesSummary.find(r => r.role === roleId);
    return summary ? summary.count : 0;
  };

  if (!selectedPharmacy) {
    return (
      <div className="flex items-center justify-center p-16 text-gray-400">
        <div className="text-center">
          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Chargement des pharmacies...</p>
        </div>
      </div>
    );
  }

  let STATS = [
    { label: 'Tous', value: users.length, icon: Users, color: 'text-slate-500', roleId: 'all' }
  ];

  if (getRoleCount('super_admin') > 0) STATS.push({ label: 'Super Admin', value: getRoleCount('super_admin'), icon: UserCog, color: 'text-purple-500', roleId: 'super_admin' });
  if (getRoleCount('admin') > 0) STATS.push({ label: 'Admin', value: getRoleCount('admin'), icon: UserCog, color: 'text-blue-500', roleId: 'admin' });
  if (getRoleCount('owner') > 0) STATS.push({ label: 'Propriétaire', value: getRoleCount('owner'), icon: UserCog, color: 'text-indigo-500', roleId: 'owner' });
  if (getRoleCount('gerant') > 0) STATS.push({ label: 'Gérant', value: getRoleCount('gerant'), icon: UserCog, color: 'text-indigo-500', roleId: 'gerant' });
  if (getRoleCount('comptable') > 0) STATS.push({ label: 'Comptable', value: getRoleCount('comptable'), icon: UserCog, color: 'text-yellow-500', roleId: 'comptable' });
  if (getRoleCount('caissier') > 0 || STATS.length === 1) STATS.push({ label: 'Caissiers', value: getRoleCount('caissier'), icon: User, color: 'text-emerald-500', roleId: 'caissier' });
  if (getRoleCount('assistant') > 0) STATS.push({ label: 'Assistants', value: getRoleCount('assistant'), icon: User, color: 'text-teal-500', roleId: 'assistant' });

  const customPermsCount = users.filter(u => u.permissions_explicitly_set).length;
  if (customPermsCount > 0) {
    STATS.push({ label: 'Perso.', value: customPermsCount, icon: UserCog, color: 'text-orange-500', roleId: 'custom_perms' });
  }

  const handleStatClick = (roleId) => {
    setRoleFilter(roleId);
    setActiveTab('utilisateurs');
  };

  const filteredUsers = roleFilter === 'all' ? users 
    : roleFilter === 'custom_perms' ? users.filter(u => u.permissions_explicitly_set)
    : users.filter(u => u.role === roleFilter || (u.pharmacies && u.pharmacies.some(p => p.pharmacy_id === selectedPharmacy.id && p.role === roleFilter)));

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-3 sm:p-6 bg-gray-50 min-h-screen">

      {/* En-tête standard */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Utilisateurs</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Gérez vos utilisateurs, rôles et permissions</p>
        </div>

      </div>

      {/* Layout 2 colonnes */}
      <div className="flex gap-4 sm:gap-6 items-start">

        {/* Colonne gauche : Liste des pharmacies (visible seulement s'il y en a plusieurs) */}
        {pharmacies.length > 1 && (
          <div className="w-48 shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-4">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Pharmacies</p>
                <p className="text-xs text-gray-400 mt-0.5">{pharmacies.length} pharmacies</p>
              </div>
              <div className="p-2 space-y-1 max-h-[65vh] overflow-y-auto">
                {pharmacies.map(pharm => (
                  <button
                    key={pharm.id}
                    onClick={() => setSelectedPharmacy(pharm)}
                    data-testid={`pharmacy-selector-${pharm.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
                      selectedPharmacy?.id === pharm.id
                        ? 'bg-emerald-50 border border-emerald-100'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 ${
                      selectedPharmacy?.id === pharm.id
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {(pharm.name || 'P').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${selectedPharmacy?.id === pharm.id ? 'text-emerald-700' : 'text-gray-700'}`}>
                        {pharm.name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{pharm.address || 'Pharmacie'}</p>
                    </div>
                    {selectedPharmacy?.id === pharm.id && (
                      <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Colonne droite : Contenu de la pharmacie sélectionnée */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* Carte info pharmacie + stats */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Store className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">{selectedPharmacy.name}</h2>
                  <p className="text-xs text-gray-500">{selectedPharmacy.address || 'Setif, Algérie'}</p>
                </div>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700 ml-1">
                  Active
                </span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                {STATS.map((stat) => (
                  <button 
                    key={stat.label} 
                    onClick={() => handleStatClick(stat.roleId)}
                    className={`flex items-center gap-2 border rounded-xl px-3 py-2 transition-all hover:bg-gray-100 ${roleFilter === stat.roleId && activeTab === 'utilisateurs' ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-gray-100 bg-gray-50'}`}
                  >
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                    <div className="text-left">
                      <p className="text-sm font-bold text-gray-800 leading-none">{stat.value}</p>
                      <p className="text-[9px] uppercase font-bold tracking-wider text-gray-400">{stat.label}</p>
                    </div>
                  </button>
                ))}
                <Button variant="outline" size="sm" onClick={() => setActiveTab('informations')} className="text-slate-700 text-xs h-8">
                  <Edit className="w-3.5 h-3.5 mr-1.5" />
                  Modifier
                </Button>
              </div>
            </div>
          </div>

          {/* Carte onglets + contenu */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Barre des onglets */}
            <div className="border-b border-gray-100 px-4 sm:px-6">
              <div className="flex gap-1 sm:gap-6">
                {TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    data-testid={`tab-${tab.key}`}
                    className={`py-3 sm:py-4 px-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.key
                        ? 'border-emerald-500 text-emerald-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contenu des onglets */}
            {activeTab === 'roles' && (
              <RolesAndPermissionsView
                pharmacy={selectedPharmacy}
                currentUser={currentUser}
                rolesSummary={rolesSummary}
                users={users}
                onRefresh={refreshData}
              />
            )}
            {activeTab === 'utilisateurs' && (
              <div className="p-4 sm:p-6">
                <UsersTab
                  users={filteredUsers}
                  pharmacy={selectedPharmacy}
                  currentUser={currentUser}
                  onRefresh={refreshData}
                />
              </div>
            )}
            {activeTab === 'informations' && (
              <InformationsTab
                pharmacy={selectedPharmacy}
                users={users}
                rolesSummary={rolesSummary}
                currentUser={currentUser}
                onRefresh={refreshData}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersManagement;
